import {
  CONFIG_PERMISSION_CORE_BASE_URL,
  CONFIG_PERMISSION_CORE_PATH,
  CONFIG_ADITION,
  CONFIG_CORE_IFRAME_CLASSNAME,
  CONFIG_VERSION, CONFIG_ENVIRONMENT
} from '../../config';

import Penpal, { IConnectionObject, ConnectionMethods } from 'penpal';
import { AditionClientConfig, LayerParameters, PermissionModel } from '../../interfaces';
import { PermissionCore } from '../../core/interfaces';
import { PermissionCoreIframe } from './core-iframe.interface';
import { Dimensions, PermissionClientOptions } from '../interfaces';
import { PermissionClientMessaging } from './interfaces';
import * as Logger from '../../common/tools/logger';
import { LayerService } from './layer.service';
import { PermissionLayerOptions } from '../../layer/interfaces';
import { PpsConfig } from '../../core/tools/pps.service';
import { getTimeout } from '../../common/tools/util';

/**
 * Permission Core Service
 */
export class CoreService {

  public layerService: LayerService;

  private moduleName = `CoreService`;
  private options: PermissionClientOptions;
  private iframe: HTMLIFrameElement;
  private permissionCore: PermissionCore;
  private childConnection: IConnectionObject<ConnectionMethods>;
  private clientApi: PermissionClientMessaging = {
    openPermissionLayer: (options: PermissionLayerOptions): void => {
      Logger.info(this.moduleName, `initializeCommandListener`, `PermissionClient received command 'openPermissionLayer'. Going to open the permission layer, Options:`, options);

      /**
       * Fix for PIM-2794 / TECSOD-5254
       * Show the permission core only if ...
       * ... Penpal was able to connect to the layer.
       * Otherwise it remains hidden.
       */
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (this.iframe && this.childConnection && (this.childConnection as any).iframe as HTMLIFrameElement && this.iframe === (this.childConnection as any).iframe) {
        this.showIframe();
      }

      this.options.onShowLayer();
    },

    closePermissionLayer: (): void => {
      Logger.info(this.moduleName, `initializeCommandListener`, `PermissionClient received command 'closePermissionLayer'. Finishing Lifecycle.`);
      this.options.onComplete(true, null);
      this.destroy();
    },

    setPermissions: async (model: PermissionModel): Promise<void> => {
      Logger.info(this.moduleName, 'setPermission', `Publisher page received the 'setPermissions' command, calling 'onChange'`, model);
      await this.options.onChange(model);
    },

    resizePermissionLayer: (dimensions: Dimensions): void => {
      Logger.info(this.moduleName, 'resizePermissionLayer', `Publisher page received the 'resizePermissionLayer command', going to resize the layer`, dimensions.height);
      if (typeof dimensions.height === 'number') {
        this.iframe.style.height = dimensions.height + 'px';
      } else if (typeof dimensions.height === 'string') {
        this.iframe.style.height = dimensions.height;
      }

      this.options.onResizeLayer(dimensions);
    },

    getAditionClientConfig: (): AditionClientConfig => {
      return {
        portal: this.options.portal,
        category: this.options.category,
        section: this.options.section ? this.options.section : this.options.category,
        tagid: this.options.tagid ? this.options.tagid : CONFIG_ADITION.DEFAULT.TAGID,
        layoutclass: this.options.layoutclass ? this.options.layoutclass : CONFIG_ADITION.DEFAULT.LAYOUTCLASS,
        ... this.options.permissionGroup ? { permissionGroup: this.options.permissionGroup } : null,
        ... this.options.forcePermissionLayer ? { forcePermissionLayer: this.options.forcePermissionLayer } : null,
        stage: this.options.stage
      }
    },

    getParameters: (): LayerParameters => {
      return {
        portal: this.options.portal,
        category: this.options.category,
        section: this.options.section,
        consentParameters: this.options.consentParameters,
        permissionGroup: this.options.permissionGroup,
        forcePermissionLayer: this.options.forcePermissionLayer
      };
    },

    getIdentity: (): string => {
      return this.options.getIdentity();
    },

    getModel: (identity: string): PermissionModel => {
      return this.options.getModel(identity);
    }
  };

  /**
   * @public
   * @async
   * @function init
   * @description Function to initialize the Permission Core Application
   * @param {PermissionClientOptions} options Optional options
   * @return {Promise<void>}
   */
  public async init(options: PermissionClientOptions): Promise<void> {
    try {
      this.options = options;

      // Wait until the DOM is ready before inserting the Permission Core iframe
      if (document.readyState === 'loading') {
        await new Promise((resolve): void => {
          window.addEventListener('DOMContentLoaded', () => {
            resolve();
          });
        });
      }

      /* istanbul ignore next else */
      if (!this.layerService) {
        this.layerService = new LayerService(this.options.layerContainer);
      } else {
        // TODO: update the service's layerContainer
      }

      await this.loadPermissionCore();

      Logger.debug(this.moduleName, `initialize`, `Module initialized.`);
    } catch (error) {
      this.clientApi.closePermissionLayer();
      Logger.error(this.moduleName, `initialize`, error);
    }
  }

  public async layerDisplayed(): Promise<void> {
    await this.permissionCore.layerDisplayed();
  }

  /**
   * @public
   * @function destroy
   * @description Reset all references so this instance can be reinitialized with a different set of options
   * @return {void}
   */
  public destroy(): void {
    /* istanbul ignore next else */
    if (this.childConnection) {
      this.childConnection.destroy();
      this.childConnection = null;
    }

    /* istanbul ignore next else */
    if (this.layerService) {
      this.layerService.removeLayer();
      this.layerService = null;
    }

    /* istanbul ignore next else */
    if (this.iframe) {
      this.iframe.src = 'about:blank';
      if (this.iframe.parentNode) {
        this.iframe.parentNode.removeChild(this.iframe);
      }
      this.iframe = null;
    }
  }

  /**
   * @private
   * @async
   * @function loadPermissionCore
   * @description Function to load / initialize the Permission Core iframe
   * @return {Promise<void>}
   */
  private async loadPermissionCore(): Promise<void> {
    try {
      if (this.childConnection) {
        // Core is already loaded, update it
        const ppsConfig: PpsConfig = {
          accessToken: this.options.accessToken,
          touchpointName: this.options.touchpointName,
          touchpointReference: this.options.touchpointReference,
          xUIApp: this.options.xUIApp,
          portal: this.options.portal
        };
        await this.permissionCore.initializePermissionLayer(ppsConfig);
      }
    } catch (ex) {
      Logger.warn(this.moduleName, `loadPermissionCore`, `Failed to use existing connection, creating a new one.`);
    }

    try {
      const appendTo = this.layerService.permissionLayer;

      // Workaround for SWAT-2135
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (typeof (Penpal as any).Promise === 'undefined') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (Penpal as any).Promise = window.Promise;
      }

      this.childConnection = Penpal.connectToChild({
        url: this.getCoreUrl(),
        iframe: this.iframe = this.createIframe(),
        appendTo,
        timeout: getTimeout(),
        methods: this.clientApi
      });

      this.permissionCore = await this.childConnection.promise as PermissionCore;
      const ppsConfig: PpsConfig = {
        accessToken: this.options.accessToken,
        touchpointName: this.options.touchpointName,
        touchpointReference: this.options.touchpointReference,
        xUIApp: this.options.xUIApp,
        portal: this.options.portal
      };

      await this.permissionCore.initializePermissionLayer(ppsConfig);

    } catch (error) {
      this.clientApi.closePermissionLayer();
      Logger.error(this.moduleName, `loadPermissionCore`, `Failed to load permission core.`, error);
    }
  }

  /**
   * @protected
   * @function createIframe
   * @description Function to create a new Permission Core iframe
   * @return {HTMLIFrameElement}
   */
  protected createIframe(): HTMLIFrameElement {
    const iframe = document.createElement('iframe');
    const iframeAttributes: PermissionCoreIframe = {
      class: CONFIG_CORE_IFRAME_CLASSNAME,
      width: '100%',
      border: '0',
      frameborder: '0',
      marginwidth: '0',
      marginheight: '0',
      allowtransparency: 'true',
      background: 'transparent',
      scrolling: 'no',
      style: 'position: absolute; top: -99999px; left: -99999px; display: block; width: 100%; height: 100vh; border: 0; z-index: 2147483647',
      ...( typeof this.options.section !== 'undefined' && { name: this.options.section })
    };

    Object.keys(iframeAttributes).forEach((attr) => {
      iframe.setAttribute(attr, iframeAttributes[attr as keyof PermissionCoreIframe]);
    });

    Logger.debug(this.moduleName, 'createIframe', 'New Iframe created', iframe);

    return iframe;
  }

  /**
   * Apply style changes to the iframe to be shown in the users viewport
   */
  private showIframe(): void {
    if (!this.iframe) {
      Logger.error(this.moduleName, 'showIframe', 'There is no iframe to be shown');
      return;
    }
    this.iframe.style.position = 'fixed';
    this.iframe.style.top = 'auto';
    this.iframe.style.bottom = '0';
    this.iframe.style.left = '0';
    this.iframe.style.right = 'auto';
  }

  /**
   * Get the permission core URL for the current stage/version.
   */
  protected getCoreUrl(): string {

    /* istanbul ignore next if */
    // The value of CONFIG_ENVIRONMENT is replaced at build time which confuses tsc so we have to ts-ignore it
    // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
    // @ts-ignore
    if (CONFIG_ENVIRONMENT === 'DEVELOPMENT') {
      return `./${CONFIG_PERMISSION_CORE_PATH}`;
    }

    const baseUrl = CONFIG_PERMISSION_CORE_BASE_URL[this.options.portal];
    if (!baseUrl) {
      throw new Error(`${this.moduleName}: Failed to get core url for portal '${this.options.portal}'!`)
    }

    let stage = 'live';
    if (this.options.stage) {
      stage = this.options.stage;
    } else /* istanbul ignore next if */ if (String(CONFIG_ENVIRONMENT) === 'QA') {
      stage = 'qa';
    }

    return `${baseUrl}/${stage}/v${CONFIG_VERSION}/${CONFIG_PERMISSION_CORE_PATH}`;
  }

}
